import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Film, Wand2, ArrowRight, Sparkles } from "lucide-react";

export default function OnboardingStart() {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const cards = [
        {
            key: "video",
            Icon: Film,
            accent: "#7C3AED",
            badge: t("onboarding.video_badge"),
            title: t("onboarding.video_title"),
            desc: t("onboarding.video_desc"),
            cta: t("onboarding.video_cta"),
            to: "/tools/video-tour?welcome=1",
            testId: "onboarding-video-card",
        },
        {
            key: "retouch",
            Icon: Wand2,
            accent: "#E5A93D",
            badge: t("onboarding.retouch_badge"),
            title: t("onboarding.retouch_title"),
            desc: t("onboarding.retouch_desc"),
            cta: t("onboarding.retouch_cta"),
            to: "/tools/photo-retouch?welcome=1",
            testId: "onboarding-retouch-card",
        },
    ];

    return (
        <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6 py-14" data-testid="onboarding-start-page">
            <div className="max-w-4xl w-full">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="text-center mb-10"
                >
                    <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#E5A93D] font-semibold mb-4">
                        <Sparkles className="h-3.5 w-3.5" />
                        {t("onboarding.eyebrow")}
                    </div>
                    <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-zinc-950 mb-4">
                        {t("onboarding.title_1")}{" "}
                        <span className="text-[#E5A93D] italic font-medium">{t("onboarding.title_highlight")}</span>
                    </h1>
                    <p className="text-zinc-500 text-base max-w-2xl mx-auto leading-relaxed">
                        {t("onboarding.subtitle")}
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {cards.map((c, i) => (
                        <motion.button
                            key={c.key}
                            type="button"
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.12 + i * 0.1 }}
                            onClick={() => navigate(c.to)}
                            data-testid={c.testId}
                            className="group text-left bg-white border border-zinc-200 hover:border-[#E5A93D]/60 rounded-md p-7 transition-colors flex flex-col"
                        >
                            <div className="flex items-center justify-between mb-5">
                                <div
                                    className="h-11 w-11 rounded-sm flex items-center justify-center border"
                                    style={{ backgroundColor: `${c.accent}14`, borderColor: `${c.accent}40` }}
                                >
                                    <c.Icon className="h-5 w-5" style={{ color: c.accent }} />
                                </div>
                                <span
                                    className="text-[10px] uppercase tracking-[0.18em] font-bold px-2.5 py-1 rounded-sm border"
                                    style={{ color: c.accent, borderColor: `${c.accent}50`, backgroundColor: `${c.accent}0d` }}
                                >
                                    {c.badge}
                                </span>
                            </div>
                            <h2 className="font-display text-2xl font-bold tracking-tight text-zinc-950 mb-2">{c.title}</h2>
                            <p className="text-sm text-zinc-500 leading-relaxed mb-6 flex-1">{c.desc}</p>
                            <span className="inline-flex items-center gap-2 h-11 px-5 bg-zinc-950 group-hover:bg-[#E5A93D] text-white group-hover:text-black font-bold rounded-sm transition-colors self-start text-sm">
                                {c.cta}
                                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                            </span>
                        </motion.button>
                    ))}
                </div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.45 }}
                    className="text-center mt-8"
                >
                    <button
                        type="button"
                        onClick={() => navigate("/dashboard")}
                        data-testid="onboarding-skip-btn"
                        className="text-sm text-zinc-500 hover:text-zinc-950 underline underline-offset-4 transition-colors"
                    >
                        {t("onboarding.skip")}
                    </button>
                </motion.div>
            </div>
        </div>
    );
}
